import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { fmtMoney, STATUS_COLORS } from "@/lib/pharmacy";
import { reorderIntoCart } from "@/lib/reorder";
import { useCart } from "@/lib/cart";
import { RotateCcw } from "lucide-react";
import { toast } from "sonner";

export default function MyOrders() {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(null);
  const { addItem } = useCart();
  const nav = useNavigate();

  useEffect(() => { api.get("/orders/mine").then((r) => setRows(r.data)); }, []);

  const reorder = async (o) => {
    setBusy(o.id);
    try {
      const { added, skipped } = await reorderIntoCart(o, addItem);
      if (!added.length) return toast.error("None of these items are available anymore");
      if (skipped.length) toast.warning(`${added.length} item(s) added, ${skipped.length} skipped`);
      else toast.success(`${added.length} item(s) added to cart`);
      nav("/cart");
    } finally { setBusy(null); }
  };

  return (
    <div className="blz-container py-10">
      <h1 className="font-display text-3xl font-bold">My orders</h1>
      <div className="mt-6 card-flat">
        {rows.length === 0 ? (
          <div className="p-10 text-center text-slate-500">You have no orders yet. <Link to="/catalogue" className="text-emerald-700 font-semibold">Start shopping →</Link></div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500">
              <tr><th className="text-left p-3">Order</th><th className="text-left p-3">Date</th><th className="text-left p-3">Items</th><th className="text-left p-3">Total</th><th className="text-left p-3">Status</th><th></th></tr>
            </thead>
            <tbody>
              {rows.map((o) => (
                <tr key={o.id} className="border-t border-slate-100" data-testid={`myorder-${o.order_number}`}>
                  <td className="p-3 font-mono font-semibold">{o.order_number}</td>
                  <td className="p-3 text-slate-500">{o.created_at.slice(0,10)}</td>
                  <td className="p-3">{o.items.length} · {o.total_pills} pills</td>
                  <td className="p-3 font-mono">{fmtMoney(o.grand_total)}</td>
                  <td className="p-3"><span className={`text-xs px-2 py-1 rounded-full ${STATUS_COLORS[o.status] || "bg-slate-100"}`}>{o.status}</span></td>
                  <td className="p-3 text-right whitespace-nowrap">
                    {o.status === "Delivered" && (
                      <button
                        onClick={() => reorder(o)}
                        disabled={busy === o.id}
                        className="inline-flex items-center gap-1 text-emerald-700 font-semibold mr-4 disabled:opacity-50"
                        data-testid={`reorder-${o.order_number}`}
                      >
                        <RotateCcw className="h-3.5 w-3.5"/> {busy === o.id ? "Adding..." : "Reorder"}
                      </button>
                    )}
                    <Link to={`/my-orders/${o.order_number}`} className="text-slate-700 font-semibold">View →</Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
